<template>
    <AdminPageInner v-if="loaded" :breadcrumbs="breadcrumbs">
        <template #heading>Awards {{ user.name }}</template>
        <template #links>
            <router-link
                v-if="$can('create', 'Award') && $route.params.userId != $store.getters.user.id"
                :to="{name: 'admin.users.awards.create', params: {userId: $route.params.userId}}">
                Create
            </router-link>
        </template>
        <PaginationWrapper ref="pagination" :rows="rows" :constraints="constraints" :meta="meta">
            <DataComponent
                class="w-full mt-6"
                :data="rows"
                :initialSort="initialSort"
                @sort="onSort">
                <DataField name="awarded_for_type" label="Course Title" class="md:w-1/3">
                    <template v-slot="{row}">
                        <template v-if="row.awarded_for_type === null">{{ row.reason }}</template>
                        <template v-if="row.awarded_for_type === AwardedFor.SessionGroup">
                            <router-link
                                v-if="row.awarded_for.user_can.view"
                                :to="{
                                    name: 'admin.courses.session-groups.show',
                                    params: {
                                        courseId: row.awarded_for.course_id,
                                        courseSessionGroupId: row.awarded_for.id
                                    }
                                }">
                                {{ row.awarded_for.course.title }}
                            </router-link>
                        </template>
                    </template>
                </DataField>
                <DataField name="created_at" label="Date" class="md:w-1/3">
                    <template v-slot="{row}">
                        <router-link
                            v-if="row.user_can.view"
                            :to="{name: 'admin.awards.show', params: {userId: row.user_id, awardId: row.id}}">
                            <CalendarFormat :date="row.created_at"/>
                        </router-link>
                        <CalendarFormat v-else :date="row.created_at"/>
                    </template>
                </DataField>
                <DataField name="amount">
                    <template v-slot="{row}">
                        <span :class="row.amount > 0 ? 'text-green-500' : 'text-red-600'">{{ row.amount }}</span>
                    </template>
                </DataField>
                <DataField name="actions" :sortable="false" :showLabel="false" :showResponsiveLabel="false">
                    <template v-slot="{row}">
                        <div class="flex justify-around md:justify-end items-center">
                            <button
                                v-if="row.user_can.delete"
                                class="text-red-600" @click.prevent="confirmDelete(row)">Delete
                            </button>
                        </div>
                    </template>
                </DataField>
            </DataComponent>
        </PaginationWrapper>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import dayjs from "dayjs";
import AdminPageInner from "@/Components/Layout/AdminPageInner.vue";
import PaginationWrapper from "@/Components/Layout/PaginationWrapper.vue";
import PaginationPage from "@/Mixins/PaginationPage";
import {DataComponent, DataField} from "@/Components/DataComponent";
import CalendarFormat from "@/Components/CalendarFormat";
import {AwardedFor} from "@/Lib/Enums";

export default {

    name: "AwardIndexPage",

    mixins: [PaginationPage],

    data() {
        return {
            user: {},
            AwardedFor,
        };
    },

    components: {
        AdminPageInner,
        PaginationWrapper,
        DataComponent,
        DataField,
        CalendarFormat
    },

    methods: {
        setData({awards, user}) {
            PaginationPage.methods.setData.call(this, awards);
            if (user) {
                this.user = user;
            }
        },
        confirmDelete(award) {
            if (window.confirm('Are you sure you want to delete this Award?')) {
                axios.delete(route('api.awards.destroy', award))
                    .then(() => {
                        this.$notify.success('Award deleted');
                        this.$router.push({
                            name: this.$route.name,
                            query: _.assign({}, this.$route.query, {t: dayjs().unix()})
                        });
                    })
                    .catch(console.error);
            }
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {label: 'Admin', to: {name: 'admin'}},
                {label: 'Users', to: {name: 'admin.users.index'}},
                {
                    label: this.user.name || 'User',
                    to: {name: 'admin.users.show', params: {userId: this.$route.params.userId}}
                },
                {label: 'User Awards', to: {name: this.$route.name}},
            ];
        },
    },

    async beforeRouteEnter(to, from, next) {

        const data = await fetchAllData(to);
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {

        let data = {};

        if (to.params.userId !== from.params.userId) {
            data = await fetchAllData(to);
        } else {
            data.awards = (await fetchAwards(to)).data;
        }

        this.setData(data);
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        fetchAwards(to),
        axios.get(route('api.users.show', to.params.userId), {params: to.query}),
    ])

    return {
        awards: data[0].data,
        user: data[1].data
    };
}

const fetchAwards = async (to) => {
    return axios.get(route('api.awards.index'), {
        params: Object.assign(to.query, {user_id: to.params.userId})
    });
}

</script>
